import { useEffect, useRef, useState } from "react";
import {
  Button,
  DatePicker,
  FormField,
  Input,
  Modal,
  Textarea,
  toast,
} from "@playbookai/ui";
import { useRecordKpiObservation } from "../hooks/useRecordKpiObservation.js";
import type { RecordKpiObservationInput } from "../api/types.js";
import { errorMessage } from "./error-message.js";
import {
  isRecordableKpiValue,
  localInputDate,
  localInputToIso,
  nowAsLocalInput,
  withLocalInputDate,
} from "./kpi-observation-value.js";

export interface RecordKpiValueModalProps {
  readonly kpiId: string;
  /** Rendered beside the value input so the number being typed has a unit. */
  readonly unit: string;
  readonly open: boolean;
  readonly onClose: () => void;
}

/**
 * Record one KPI value (`POST /v1/kpis/:id/observations`).
 *
 * Minimal on purpose: a number and when it was observed. The observed time
 * defaults to now, so the ordinary case — "here is today's figure" — is one
 * field and a button; changing it is what backdating a Friday reading entered on
 * Monday looks like, which the server supports and orders correctly.
 *
 * The value stays a STRING the whole way through. Parsing it to a `number` to
 * "validate" it and re-serialising would round-trip through a float and could
 * send a figure the user did not type.
 */
export function RecordKpiValueModal({
  kpiId,
  unit,
  open,
  onClose,
}: RecordKpiValueModalProps): JSX.Element {
  const valueRef = useRef<HTMLInputElement>(null);
  const record = useRecordKpiObservation();
  const [value, setValue] = useState("");
  // Initialised AND re-seeded on open. The initializer keeps the first render
  // valid (an empty string would render the required-date error for one frame);
  // the effect is what makes the default actually current, because the modal
  // stays mounted between openings and a mount-time-only default would freeze at
  // whenever the page loaded and quietly backdate every later reading.
  const [observedAt, setObservedAt] = useState(nowAsLocalInput);
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) setObservedAt(nowAsLocalInput());
  }, [open]);

  const trimmed = value.trim();
  const valueError = !touched
    ? undefined
    : !trimmed
      ? "A value is required."
      : !isRecordableKpiValue(trimmed)
        ? "Enter a number with at most 14 digits and 4 decimal places."
        : undefined;
  // The parsed instant is the gate, not `Boolean(observedAt)`. `observedAt` is
  // a local wall-clock string, and there are shapes of it that are truthy and
  // unparseable — clearing the date used to produce one. That combination let
  // Save through, then omitted `observedAt` from the request, and the server
  // filed the reading at ITS now: a value recorded against a moment the user
  // never chose, silently, on an append-only ledger that cannot take it back.
  const observedAtIso = localInputToIso(observedAt);
  // Shown immediately rather than behind `touched`, unlike Value. The field is
  // seeded valid on open, so there is no pristine-but-empty state to nag about
  // — it can only be empty because the user cleared it, and Save is disabled the
  // moment it is. A disabled control with no stated reason is the pattern
  // `packages/ui/CLAUDE.md` calls out.
  const observedAtError = observedAtIso
    ? undefined
    : "A measurement date is required.";
  const canSubmit =
    isRecordableKpiValue(trimmed) &&
    Boolean(observedAtIso) &&
    !record.isPending;

  const reset = () => {
    setValue("");
    setNote("");
    setTouched(false);
  };

  const handleClose = () => {
    if (record.isPending) return;
    reset();
    onClose();
  };

  const handleSubmit = () => {
    setTouched(true);
    if (!isRecordableKpiValue(trimmed) || !observedAtIso) return;
    const input: {
      -readonly [K in keyof RecordKpiObservationInput]?: RecordKpiObservationInput[K];
    } = { value: trimmed, observedAt: observedAtIso };
    // ALWAYS sent. There is no branch that omits it: the field is required, the
    // guard above refuses an unparseable one, and an omitted `observedAt` would
    // let the server choose the instant instead of the person recording it.
    if (note.trim()) input.note = note.trim();
    record.mutate(
      { kpiId, input: input as RecordKpiObservationInput },
      {
        onSuccess: () => {
          toast("Value recorded", { tone: "healthy" });
          reset();
          onClose();
        },
        onError: (err) =>
          toast(errorMessage(err, "Could not record the value."), {
            tone: "critical",
          }),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="sm"
      initialFocusRef={valueRef}
      title="Add value"
      description="Records a new reading. Earlier readings are kept — a correction is recorded as another reading, never an edit."
      footer={
        <>
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={record.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={record.isPending}
          >
            Save value
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField
          label="Value"
          required
          error={valueError}
          {...(unit ? { hint: `Measured in ${unit}.` } : {})}
        >
          <Input
            ref={valueRef}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onBlur={() => setTouched(true)}
            inputMode="decimal"
            placeholder="e.g. 118.5"
            {...(unit ? { suffix: unit } : {})}
          />
        </FormField>
        <FormField
          label="Observed at"
          required
          error={observedAtError}
          hint="Defaults to now. Set it earlier to record a reading taken before today."
        >
          <DatePicker
            value={localInputDate(observedAt)}
            onChange={(date) =>
              setObservedAt((current) => withLocalInputDate(current, date))
            }
            placeholder="Pick a measurement date"
          />
        </FormField>
        <FormField label="Note">
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            maxLength={500}
            showCount
          />
        </FormField>
      </div>
    </Modal>
  );
}
